/**
 * Health Data API Hooks
 *
 * Overview of health indicators, disease surveillance
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'

// ============================================
// Types
// ============================================

export interface HealthOverview {
  // Insurance coverage
  coverage_rate: number
  uc_members: number
  sso_members: number
  csmbs_members: number

  // Active patients & visits
  active_patients: number
  opd_visits: number
  ipd_visits: number
  er_visits: number

  // Disease surveillance (epidemic)
  surveillance_cases: number
  dengue: number
  hfmd: number
  other_diseases: number

  // Chronic diseases (NCD)
  ncd_patients: number
  diabetes: number
  hypertension: number
  cvd: number
}

export interface HealthDistrict {
  district_id: number
  district_name: string
  total_cases: string
  coverage_rate: string
  by_disease: Array<{
    disease_name_th: string
    case_count: string
  }>
}

// ============================================
// API Hooks
// ============================================

/**
 * Get health data overview (provincial level)
 */
export function useHealthOverview(year: number) {
  const { data, error, isLoading } = useSWR<HealthOverview>(
    [`/health-data/dashboard`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // 1 minute
    }
  )

  return {
    data,
    error,
    isLoading,
  }
}

/**
 * Get health data by district
 */
export function useHealthByDistrict(districtId: number, year: number) {
  const shouldFetch = districtId > 0

  const { data, error, isLoading } = useSWR<HealthDistrict>(
    shouldFetch
      ? [`/health-data/by-district/${districtId}`, { year: year.toString() }]
      : null,
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000,
    }
  )

  return {
    data,
    error,
    isLoading,
  }
}

/**
 * Get multi-year health trends (5-10 years)
 */
export function useHealthYearlyTrends(filters?: {
  districtId?: number
  diseaseId?: number
}) {
  const params: Record<string, string> = {}

  if (filters?.districtId) {
    params.district_id = filters.districtId.toString()
  }
  if (filters?.diseaseId) {
    params.disease_id = filters.diseaseId.toString()
  }

  const { data, error, isLoading, mutate } = useSWR<{
    filters: typeof params
    trends: Array<{
      year: number
      total_cases: number
      coverage_rate: number
      active_patients: number
    }>
  }>(
    [`/health-data/yearly-trends`, params],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // Cache for 1 minute
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}
